/** Opaque engine frames use canonical base64 on the JSON wire. No engine dependency. */
import { z } from 'zod'
import { maxPageEntries } from './limits'
export { replicationLimits, maxPageEntries, type ReplicationLimits } from './limits'
import type { DatabaseDriver } from '@w/db'

const integer = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
export const valueSchema = z.union([z.string(), z.number(), z.null()])
export const columnsSchema = z.record(z.string(), valueSchema)
export const mutationSchema = z.object({
  table: z.string().min(1).max(256), pk: z.string().min(1).max(256), kind: z.enum(['insert', 'update', 'delete', 'cover']),
  epoch: integer, covers: integer.optional(), columns: columnsSchema, flags: z.array(z.string()).max(100), bytes: z.string().optional(),
})
export const pushSchema = z.object({ workspace: z.string().min(1).max(128), device: z.string().min(1).max(512), txn: integer, since: integer, rows: z.array(mutationSchema) })
export const resultSchema = z.discriminatedUnion('status', [
  z.object({ index: integer, pk: z.string(), status: z.literal('accepted'), seq: integer }),
  z.object({ index: integer, pk: z.string(), status: z.literal('stale-epoch'), epoch: integer }),
  z.object({ index: integer, pk: z.string(), status: z.literal('stale-cover'), latest: integer }),
  z.object({ index: integer, pk: z.string(), status: z.literal('invalid-frame') }),
  z.object({ index: integer, pk: z.string(), status: z.literal('unknown-row') }),
])
export const logSchema = mutationSchema.extend({ workspace: z.string(), seq: integer, device: z.string(), txn: integer, index: integer })
export const ackSchema = z.object({ workspace: z.string(), device: z.string(), txn: integer, seq: integer, through: integer, log: z.array(logSchema).max(maxPageEntries), results: z.array(resultSchema) })
export const pullRequestSchema = z.object({ workspace: z.string().min(1).max(128), device: z.string().min(1).max(512), since: integer, cursor: integer.optional() })
export const pullResponseSchema = z.object({ workspace: z.string(), since: integer, through: integer, head: integer, log: z.array(logSchema).max(maxPageEntries) })
export type Columns = z.infer<typeof columnsSchema>
export type Mutation = z.infer<typeof mutationSchema>
export type Push = z.infer<typeof pushSchema>
export type Ack = z.infer<typeof ackSchema>
export type Result = z.infer<typeof resultSchema>
export type Log = z.infer<typeof logSchema>
export type PullRequest = z.infer<typeof pullRequestSchema>
export type Pull = z.infer<typeof pullResponseSchema>
export type PullResponse = Pull
export type SqlValue = string | number | bigint | null | Uint8Array
export type Statement = { sql: string; params?: unknown[] }
export interface SqlPort {
  query<T>(sql: string, params?: unknown[]): Promise<T[]>
  /** Exactly one atomic transaction, returning each statement's rows in order.
   * Reject before publishing if deployment limits are exceeded. */
  batch(statements: Statement[]): Promise<unknown[][]>
}
export type ClientSql = Pick<DatabaseDriver, 'query' | 'exec' | 'transaction'>
export type Table = { name: string; primaryKey: string; columns: { name: string; type: 'text' | 'integer' | 'real' | 'blob' | 'crdt' }[] }
export type Auth = { caller: string; device: string }
export type Membership = (workspace: string, caller: string) => boolean | Promise<boolean>

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCodePoint(byte)
  return btoa(binary)
}
export function base64ToBytes(value: unknown): Uint8Array {
  if (typeof value !== 'string') throw new Error('Expected base64 frame')
  const binary = atob(value)
  const bytes = Uint8Array.from(binary, char => char.codePointAt(0) as number)
  if (bytesToBase64(bytes) !== value) throw new Error('Noncanonical base64')
  return bytes
}
/** Sorted JSON object keys; array order (including assignment flags) remains significant. */
export function canonical(value: unknown): string {
  function normalize(input: unknown): unknown {
    if (Array.isArray(input)) return input.map(child => normalize(child))
    if (input && typeof input === 'object') return Object.fromEntries(Object.entries(input).filter(([, child]) => child !== undefined).toSorted(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([key, child]) => [key, normalize(child)]))
    return input
  }
  return JSON.stringify(normalize(value))
}
export function rowKey(row: Pick<Mutation, 'table' | 'pk'>): string { return JSON.stringify([row.table, row.pk]) }
export function identifier(name: string): string { return `"${name.replaceAll('"', '""')}"` }
export const REPLICATED_SYSTEM_TABLES = new Set(['__cdb_ddl_log'])
export function tableMap(tables: Table[]): Map<string, Table> {
  const map = new Map<string, Table>()
  for (const table of tables) {
    if ([...map.keys()].some(name => name.toLowerCase() === table.name.toLowerCase()) || (table.name.startsWith('_') && !REPLICATED_SYSTEM_TABLES.has(table.name)) || table.name.startsWith('sqlite_') || table.name === 'turso_cdc') throw new Error('Invalid replication table')
    if (new Set(table.columns.map(c => c.name)).size !== table.columns.length || table.columns.find(c => c.name === table.primaryKey)?.type !== 'text') throw new Error('Replication requires a declared text primary key')
    if (table.columns.filter(c => c.type === 'crdt').length > 1) throw new Error('Replication supports one CRDT column per row')
    map.set(table.name, table)
  }
  return map
}
/** A violated setup invariant: a defect, never a sync outcome, so callers neither retry nor swallow it. */
export class ReplicationInvariantError extends Error {}
/** Unambiguous authenticated namespace; supplied device ids never own server keys. */
export function deviceNamespace(userId: string, device: string): string { return JSON.stringify([userId, device]) }
